import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../services/api";
import { useState, useEffect } from "react";

export default function ProfilePage() {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [message, setMessage] = useState("");

  const { data: profileData, isLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const response = await api.get("/users/me");
      return response.data;
    },
  });

  const user = profileData?.data.user;

  useEffect(() => {
    if (user?.profile) {
      setDisplayName(user.profile.display_name || "");
      setAvatarUrl(user.profile.avatar_url || "");
    }
  }, [user]);

  const updateMutation = useMutation({
    mutationFn: async (updates: { display_name?: string; avatar_url?: string }) => {
      const response = await api.patch("/users/me/profile", updates);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setMessage("Profile updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(err.response?.data?.error || "Failed to update profile");
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-400">Loading profile...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="card">
        <p className="text-gray-400">Profile not found.</p>
      </div>
    );
  }

  const isSuccess = message.includes('success') || message.includes('successfully');

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-100 mb-8">Profile</h1>

      {message && (
        <div className={`mb-4 p-4 rounded-lg border ${isSuccess ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
          {message}
        </div>
      )}

      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Account Information</h2>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-400">Email: </span>
            <span className="text-gray-200">{user.email}</span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-400">Role: </span>
            <span className="text-gray-200 capitalize">{user.role.replace("_", " ")}</span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-400">Status: </span>
            <span className="text-gray-200 capitalize">{user.status}</span>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Edit Profile</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateMutation.mutate({ display_name: displayName, avatar_url: avatarUrl || undefined });
          }}
          className="space-y-4 max-w-md"
        >
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-2">
              Display Name
            </label>
            <input
              id="displayName"
              type="text"
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="avatarUrl" className="block text-sm font-medium text-gray-300 mb-2">
              Avatar URL
            </label>
            <input
              id="avatarUrl"
              type="url"
              className="input"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://example.com/avatar.png"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}
